import { apiClient, unwrap } from './client';
import { runPathId } from './runPath';
import type { ApiEnvelope } from './types';

/** OL-31: suggestions on a case (PROPOSAL-ol-31-case-assist.md). Nothing here can change a case. */
export type AssistKind = 'CASE_SUMMARY' | 'FOLLOW_UP_ANALYSIS' | 'TRIAGE';
export type AssistAvailability = 'READY' | 'NOT_CONFIGURED' | 'KILLED' | 'OVER_BUDGET';

export interface AssistRef {
  type: 'EVIDENCE' | 'STEP' | 'ROOM_STEP' | 'ACTION' | 'WORK_ITEM' | 'RUN';
  id: string;
  label: string;
}

export interface AssistClaim {
  text: string;
  refs: AssistRef[];
  attention?: 'HIGH' | 'MEDIUM' | 'LOW' | null;
}

export interface CaseSuggestion {
  suggestionId: string;
  kind: AssistKind;
  label: 'SUGGESTION';
  builtBy: 'MODEL' | 'CASE_RECORD';
  degradeReason?: string | null;
  claims: AssistClaim[];
  limits?: string | null;
  stale: boolean;
  erased: boolean;
  recorded: boolean;
  modelId?: string | null;
  tokens: number;
  costMicrosUsd: number;
  requestedBy: string;
  createdAt: string;
}

export interface AssistState {
  availability: AssistAvailability;
  modelConfigured: boolean;
  killedBy?: string | null;
  killedAt?: string | null;
  killReason?: string | null;
  canRequest: boolean;
  hasSubject: boolean;
}

export interface CaseAssistView {
  state: AssistState;
  suggestions: CaseSuggestion[];
}

export interface AssistControlChange {
  control: 'KILL_SWITCH' | 'BUDGET';
  before: string;
  after: string;
  reason: string;
  actor: string;
  at: string;
}

export interface AssistSettings {
  availability: AssistAvailability;
  modelConfigured: boolean;
  killed: boolean;
  killedBy?: string | null;
  killedAt?: string | null;
  killReason?: string | null;
  monthlyTokens: number;
  monthlyCostMicrosUsd: number;
  spentTokens: number;
  spentCostMicrosUsd: number;
  periodFrom: string;
  changes: AssistControlChange[];
}

const KIND_PATH: Record<AssistKind, string> = {
  CASE_SUMMARY: 'case-summary',
  FOLLOW_UP_ANALYSIS: 'follow-up-analysis',
  TRIAGE: 'triage',
};

/** What is on record and what assistance can do now. Reads only: it never builds a suggestion. */
export async function getCaseAssist(runId: string): Promise<CaseAssistView> {
  const { data } = await apiClient.get<ApiEnvelope<CaseAssistView>>(`/v1/runs/${runPathId(runId)}/assist`);
  return unwrap(data);
}

/** A person asked. The same evidence re-reads the recorded answer and spends nothing. */
export async function requestSuggestion(runId: string, kind: AssistKind): Promise<CaseSuggestion> {
  const { data } = await apiClient.post<ApiEnvelope<CaseSuggestion>>(`/v1/runs/${runPathId(runId)}/assist/${KIND_PATH[kind]}`);
  return unwrap(data);
}

export async function getAssistSettings(): Promise<AssistSettings> {
  const { data } = await apiClient.get<ApiEnvelope<AssistSettings>>('/v1/case-assist/settings');
  return unwrap(data);
}

export async function setAssistKillSwitch(killed: boolean, reason: string): Promise<AssistSettings> {
  const { data } = await apiClient.put<ApiEnvelope<AssistSettings>>('/v1/case-assist/kill-switch', { killed, reason });
  return unwrap(data);
}

export async function setAssistBudget(monthlyTokens: number, monthlyCostMicrosUsd: number, reason: string): Promise<AssistSettings> {
  const { data } = await apiClient.put<ApiEnvelope<AssistSettings>>('/v1/case-assist/budget', { monthlyTokens, monthlyCostMicrosUsd, reason });
  return unwrap(data);
}
